"use client";

import * as Sentry from "@sentry/nextjs";
import { z } from "zod";
import { create } from "zustand";

import {
  browserSessionStorage,
  type KeyValueStorage,
} from "@/stores/use-preserve-filters";

const JOB_SEARCH_ORIGIN_STORAGE_KEY = "jobSearchOrigin";
const DISTRICT_BOARD_HREF_PATTERN =
  /^\/jobs\/[^/]+\/districts\/[^/?#]+(?:\?[^#]*)?(?:#.*)?$/;

export enum JobSearchBoard {
  Statewide = "statewide",
  District = "district",
}

const jobSearchOriginSchema = z.discriminatedUnion("board", [
  z.strictObject({ board: z.literal(JobSearchBoard.Statewide) }),
  z.strictObject({
    board: z.literal(JobSearchBoard.District),
    href: z
      .string()
      .regex(
        DISTRICT_BOARD_HREF_PATTERN,
        "District href must be a district job-board path",
      ),
  }),
]);

const storedOriginSchema = z
  .string()
  .transform((stored, ctx): unknown => {
    try {
      const parsed: unknown = JSON.parse(stored);
      return parsed;
    } catch {
      ctx.addIssue({ code: "custom", message: "Stored origin is not JSON" });
      return z.NEVER;
    }
  })
  .pipe(jobSearchOriginSchema);

export type JobSearchOrigin = z.infer<typeof jobSearchOriginSchema>;

export const STATEWIDE_JOB_SEARCH_ORIGIN: JobSearchOrigin = {
  board: JobSearchBoard.Statewide,
};

export interface JobSearchOriginStore {
  origin: JobSearchOrigin;
  setOrigin: (origin: JobSearchOrigin) => void;
}

function readStoredOrigin(storage: KeyValueStorage): JobSearchOrigin {
  const stored = storage.get(JOB_SEARCH_ORIGIN_STORAGE_KEY);

  if (stored === null) {
    return STATEWIDE_JOB_SEARCH_ORIGIN;
  }

  const parsed = storedOriginSchema.safeParse(stored);

  if (!parsed.success) {
    Sentry.captureException(parsed.error);
    storage.remove(JOB_SEARCH_ORIGIN_STORAGE_KEY);
    return STATEWIDE_JOB_SEARCH_ORIGIN;
  }

  return parsed.data;
}

export function createJobSearchOriginStore(storage: KeyValueStorage) {
  const store = create<JobSearchOriginStore>()((set) => ({
    origin: readStoredOrigin(storage),
    setOrigin: (origin: JobSearchOrigin) => set({ origin }),
  }));

  store.subscribe((state) => {
    storage.set(JOB_SEARCH_ORIGIN_STORAGE_KEY, JSON.stringify(state.origin));
  });

  return store;
}

export const useJobSearchOriginStore = createJobSearchOriginStore(
  browserSessionStorage,
);

export function useJobSearchOrigin(): JobSearchOriginStore {
  const origin = useJobSearchOriginStore((state) => state.origin);
  const setOrigin = useJobSearchOriginStore((state) => state.setOrigin);

  return { origin, setOrigin };
}
